<template>
    <section class="tile relative bg-white">
        <Swiper
            :class="{ '!pb-15': data.items.length > 1 }"
            :modules="[Pagination, Navigation, A11y]"
            :pagination="{ clickable: true }"
            :navigation="{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }"
            :slides-per-view="1"
            @slide-change="handleSlideChange">
            <swiper-slide
                v-for="logo in data.items"
                :key="logo.url"
                class="flex items-center justify-center">
                <div v-if="isImage(logo)" class="aspect-[16/9] w-full">
                    <img
                        class="h-full w-full rounded object-cover lg:rounded-lg"
                        :title="logo.title ?? ''"
                        :src="logo.url + '?width=792&format=webp'"
                        :alt="logo.alt ?? ''" />
                </div>
                <div v-else-if="isVideo(logo)" class="aspect-[16/9] w-full">
                    <video
                        ref="video"
                        class="h-full w-full rounded object-cover lg:rounded-lg"
                        :title="logo.posterImage?.title ?? ''"
                        :poster="logo.posterImage?.url"
                        :alt="logo.posterImage?.alt ?? ''"
                        :src="logo.url"
                        controls
                        muted />
                </div>
            </swiper-slide>
            <div
                v-if="data.items.length > 1"
                class="absolute bottom-0 left-0 z-10 flex w-full items-center justify-between">
                <button
                    :aria-label="$t('common.previousSlide')"
                    type="button"
                    class="swiper-button-prev border-brand-black cursor-pointer rounded border p-3.5">
                    <SvgComponent icon="chevron-left" class="h-4 w-4" />
                </button>
                <button
                    :aria-label="$t('common.nextSlide')"
                    type="button"
                    class="swiper-button-next border-brand-black cursor-pointer rounded border p-3.5">
                    <SvgComponent icon="chevron-right" class="h-4 w-4" />
                </button>
            </div>
        </Swiper>
    </section>
</template>

<script setup lang="ts">
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { ImageModel, InterventionMedia, VideoItemModel } from '@su4r-frontend/api';
import type { Swiper as SwiperType } from 'swiper/types';

const { data } = defineProps<{
    data: InterventionMedia;
}>();

function isVideo(item: ImageModel | VideoItemModel): item is VideoItemModel {
    return item.alias === 'videoItem';
}

function isImage(item: ImageModel | VideoItemModel): item is ImageModel {
    return item.alias === 'imageModel';
}

const videos = useTemplateRef<HTMLVideoElement[] | null>('video');

function stopAllPlayingVideos(): void {
    if (!videos.value) return;
    videos.value.forEach((video) => {
        if (video.currentTime > 0 && !video.paused && !video.ended) {
            video.pause();
        }
    });
}

function handleSlideChange(_swiper: SwiperType): void {
    stopAllPlayingVideos();
}
</script>

<style>
@import 'swiper/css';

@import 'swiper/css/pagination';

@reference "~/assets/css/main.css";

:root {
    --swiper-pagination-color: theme(--color-brand-black);
    --swiper-pagination-bottom: 14px;
    --swiper-pagination-bullet-inactive-color: theme(--color-white);
    --swiper-pagination-bullet-inactive-opacity: 1;
}

.swiper-pagination {
    @apply right-0 z-20 mx-auto h-6 max-w-max;
}

.swiper-pagination-bullet {
    @apply border-brand-black border;
}
</style>
